'use client';

import { useRef, type ReactNode } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import OverlayDialog from './OverlayDialog';

export default function PhotoLightbox({ children, caption, index, count, onClose, onPrevious, onNext }: {
  children: ReactNode;
  caption?: ReactNode;
  index: number;
  count: number;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
}) {
  const touch = useRef<{ x: number; y: number } | null>(null);
  return (
    <OverlayDialog open onClose={onClose} label="Photo gallery" className="inset-0 m-0 h-dvh w-full bg-black/95 text-white">
      <div className="flex h-full flex-col px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6" onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); onPrevious(); }
        if (event.key === 'ArrowRight') { event.preventDefault(); onNext(); }
      }}>
        <div className="flex shrink-0 items-center justify-between gap-4 py-2">
          <p aria-live="polite" className="text-sm tabular-nums">{index + 1} / {count}</p>
          <button type="button" onClick={onClose} aria-label="Close gallery" className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/15"><X size={24} /></button>
        </div>
        <div className="relative mx-auto min-h-0 w-full max-w-6xl flex-1 touch-pan-y" onTouchStart={(event) => {
          const point = event.touches[0]; touch.current = { x: point.clientX, y: point.clientY };
        }} onTouchEnd={(event) => {
          if (!touch.current) return;
          const point = event.changedTouches[0];
          const dx = point.clientX - touch.current.x, dy = point.clientY - touch.current.y;
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) { if (dx < 0) onNext(); else onPrevious(); }
          touch.current = null;
        }}>{children}</div>
        <div className="mx-auto flex w-full max-w-4xl shrink-0 items-center gap-3 pt-3">
          <button type="button" onClick={onPrevious} aria-label="Previous photo" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 hover:bg-white/20"><ChevronLeft size={24} /></button>
          <div className="max-h-[25dvh] min-w-0 flex-1 overflow-y-auto text-center text-sm text-white/80">{caption}</div>
          <button type="button" onClick={onNext} aria-label="Next photo" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 hover:bg-white/20"><ChevronRight size={24} /></button>
        </div>
      </div>
    </OverlayDialog>
  );
}
